"use server";

import { parseWithZod } from "@conform-to/zod/v4";
import {
  premiumPaywallEnabled,
  readPremiumPaywallCookie,
  readPremiumPaywallSyncCookie,
} from "@medceptor/experiments/premium-paywall";
import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { after } from "next/server";

import { trackSignupCompleted } from "@/lib/analytics/events";
import {
  metaBrowserForRequest,
  saveMetaSignupClicks,
  sendMetaRegistration,
} from "@/lib/analytics/meta-capi";
import {
  TRACK_UNAVAILABLE_MESSAGE,
  trackIsSelectable,
} from "@/lib/auth/cert-visibility";
import { requestOrigin } from "@/lib/auth/origin";
import { requestEvidence } from "@/lib/auth/request-evidence";
import { startOAuth } from "@/lib/auth/oauth";
import { env } from "@/lib/env";
import {
  persistPremiumPaywallArm,
  premiumPaywallSignup,
  type PremiumPaywallSignup,
} from "@/lib/experiments/premium-paywall";
import { finishUrl, onboardingDeepLink, onboardingUrl } from "@/lib/onboarding/routes";
import { isShellRequest } from "@/lib/shell-server";
import { getAdminSupabase } from "@/lib/supabase/admin";
import { getServerSupabase } from "@/lib/supabase/server";
import { CERT_FOR_TRACK, encodeReferralSource } from "@/lib/onboarding/constants";
import { onboardingSchema } from "@/lib/onboarding/schema";

const FALLBACK_ERROR =
  "We couldn't save your onboarding details. Please try again or email admin@medceptor.com.";

export async function submitOnboardingAction(
  _prev: unknown,
  formData: FormData,
) {
  const submission = parseWithZod(formData, { schema: onboardingSchema });
  if (submission.status !== "success") return submission.reply();

  // /onboarding is a PUBLIC path (proxy.ts), so no proxy gate has proven a
  // session for this POST: a token that lapsed while the user was typing, or
  // a sign-out in another tab, lands here signed out. Send them back into the
  // flow (which resumes at the account step) instead of throwing.
  const deepLink = onboardingDeepLink(formData.get("next")?.toString());
  const supabase = await getServerSupabase();
  const { data: claimsData } = await supabase.auth.getClaims();
  const sub = claimsData?.claims?.sub;
  if (!sub) redirect(onboardingUrl(deepLink));

  // A second submit for an already-onboarded account (a stale tab still on
  // the About step after another tab finished) must not overwrite the saved
  // answers, move the active track, or double-count signup_completed.
  const { data: existing } = await supabase
    .from("profiles")
    .select("onboarding_completed_at, is_internal")
    .eq("id", sub)
    .maybeSingle();
  if (existing?.onboarding_completed_at) {
    redirect(finishUrl(deepLink));
  }
  const userId = sub;
  const isInternal = existing?.is_internal ?? false;

  const v = submission.value;

  // Boundary check — the client dropdown filter is UX only; see
  // trackIsSelectable. `isInternal` comes from the profile row read above
  // (one query, not getCurrentUser's fan-out).
  if (!trackIsSelectable(v.track, isInternal)) {
    console.warn(
      "[onboarding] non-internal user picked an internal-only track",
      {
        userId,
        track: v.track,
      },
    );
    return submission.reply({
      fieldErrors: { track: [TRACK_UNAVAILABLE_MESSAGE] },
    });
  }

  const referralSource = encodeReferralSource(
    v.referral_source,
    v.referral_source_other ?? "",
  );

  // Per-acceptance evidence captured for trial-defensibility (matches
  // the /legal/accept action shape).
  const { ip, userAgent } = await requestEvidence();

  const { error } = await supabase.rpc("record_onboarding", {
    p_display_name: v.display_name,
    p_organization: v.organization?.trim() || undefined,
    // Empty string lands as NULL via the RPC's NULLIF — see migration
    // 20260515035513. Can't pass null directly because the param has no
    // `default null` (would require reordering after required params).
    p_referral_source: referralSource ?? "",
    p_audience: v.audience,
    p_tos_version: v.tos_version,
    p_privacy_version: v.privacy_version,
    p_phone: v.phone?.trim() || undefined,
    p_track: v.track,
    p_ip: ip,
    p_user_agent: userAgent,
    // Guided-plan answers (migration 20261001060000). Absent for instructors,
    // written-exam-only tracks, and a resumed lost draft; the RPC defaults
    // them to NULL. `undefined` omits the key, same as p_phone above.
    p_exam_timeframe: v.exam_timeframe || undefined,
    p_concerns: v.concerns?.length ? v.concerns : undefined,
    p_weekly_scenario_goal:
      typeof v.weekly_scenario_goal === "number" ? v.weekly_scenario_goal : undefined,
  });

  if (error) {
    // 23503 on legal_acceptances_user_id_fkey = orphan JWT (profile row
    // was deleted out from under this session). Proxy normally catches
    // this first via the !profile branch and signs the user out; this
    // branch covers the race where the profile is deleted between
    // proxy check and action call. Match on constraint name — bare
    // 23503 also fires for a missing legal_documents row (deploy
    // ordering bug, not an orphan), and signing that user out would
    // be the wrong recovery. Mirrors the /legal/accept action shape.
    if (
      error.code === "23503" &&
      error.message?.includes("legal_acceptances_user_id_fkey")
    ) {
      console.warn("[onboarding] orphan JWT at action; signing out", {
        userId,
      });
      await supabase.auth.signOut({ scope: "local" });
      redirect("/login");
    }
    // Length + presence summary (not the raw values) so on-call can
    // match a CHECK violation (e.g. profiles_display_name_non_empty)
    // to which field tripped it without pulling PII from the DB.
    console.error("[onboarding] rpc failed", {
      userId,
      error,
      inputs: {
        display_name_len: v.display_name.length,
        organization_len: v.organization?.length ?? 0,
        referral_source: referralSource,
        audience: v.audience,
        track: v.track,
        has_phone: Boolean(v.phone?.trim()),
        tos_version: v.tos_version,
        privacy_version: v.privacy_version,
      },
    });
    return submission.reply({ formErrors: [FALLBACK_ERROR] });
  }

  // ENG-348: the drawn arm becomes the permanent assignment, written only after the signup is saved.
  // Shell, non-EMS track or no cookie: no row (control); a failed write reports "not_recorded", never an unstored arm.
  const cookieHeader = (await headers()).get("cookie");
  const drawn = premiumPaywallEnabled(env.EXPERIMENT_PREMIUM_PAYWALL)
    ? premiumPaywallSignup({
        cookieArm: readPremiumPaywallCookie(cookieHeader),
        syncedFor: readPremiumPaywallSyncCookie(cookieHeader),
        userId,
        shell: await isShellRequest(),
        track: v.track,
        cert: CERT_FOR_TRACK[v.track],
      })
    : null;
  const premiumPaywall: PremiumPaywallSignup | null =
    drawn?.arm &&
    !(await persistPremiumPaywallArm(getAdminSupabase(), userId, drawn.arm))
      ? { arm: null, excluded: "not_recorded" }
      : drawn;

  // Meta's CompleteRegistration from the server, deduped with the finish
  // page's browser copy (CLAUDE.md #45 j). The request is read now, the event
  // sent after the response.
  const metaBrowser = await metaBrowserForRequest();
  if (metaBrowser) {
    const origin = await requestOrigin();
    const claimedEmail = claimsData?.claims?.email;
    const email = typeof claimedEmail === "string" ? claimedEmail : null;
    after(() =>
      sendMetaRegistration({ userId, email, browser: metaBrowser, origin }),
    );
    after(() => saveMetaSignupClicks(userId, metaBrowser));
  }

  void trackSignupCompleted(userId, {
    audience: v.audience,
    track: v.track,
    referralSource,
    hasOrganization: Boolean(v.organization?.trim()),
    hasPhone: Boolean(v.phone?.trim()),
    examTimeframe: v.exam_timeframe || null,
    concerns: v.concerns ?? [],
    weeklyScenarioGoal:
      typeof v.weekly_scenario_goal === "number" ? v.weekly_scenario_goal : null,
    premiumPaywall,
  });

  // Always land on /onboarding/finish: it is the one place that knows the
  // answers are saved (it clears the pre-sign-in draft). A pending deep link
  // (`next`, e.g. a class invite or /pricing) rides along and finish forwards
  // to it; `next` is re-validated here because a Server Action is a POST
  // anyone with a session can reach.
  redirect(finishUrl(deepLink));
}

// Provider sign-in from the onboarding's account step. `next` is the account
// step's own return address (`/onboarding`, plus any pending deep link), so
// success AND failure both land back in the flow with the deep link intact.
async function onboardingOAuth(provider: "apple" | "google", formData: FormData) {
  const back = onboardingUrl(formData.get("next")?.toString());
  await startOAuth(provider, back, back);
}

export async function onboardingWithGoogleAction(formData: FormData): Promise<void> {
  await onboardingOAuth("google", formData);
}

export async function onboardingWithAppleAction(formData: FormData): Promise<void> {
  await onboardingOAuth("apple", formData);
}
